import { api } from './api';
import {
  AdminRefundResponse,
  RefundApproveRequest,
  RefundListParams,
  RefundPreviewParams,
  RefundPreviewResponse,
} from '../types/refunds';

// ── Spec 053 Flow B — refund fault attribution + consequence preview (billing svc) ──

function previewQuery(params: RefundPreviewParams): URLSearchParams {
  const query = new URLSearchParams();
  query.set('faultParty', params.faultParty);
  if (params.amount != null && !Number.isNaN(params.amount)) query.set('amount', params.amount.toFixed(2));
  if (params.feeBorneBy) query.set('feeBorneBy', params.feeBorneBy);
  if (params.commissionTreatment) query.set('commissionTreatment', params.commissionTreatment);
  // Spec 053 BR-25 — only sent when ticked; absent means "service only", which is billing's default.
  if (params.refundDeliveryFee) query.set('refundDeliveryFee', 'true');
  // Spec 068 — tri-state: absent lets billing apply the fault-party default; false must be sent.
  if (params.refundServiceFee !== undefined) query.set('refundServiceFee', String(params.refundServiceFee));
  // #716 — a dispute close completes the order first, so billing previews it as after-completion.
  if (params.closing) query.set('closing', 'true');
  return query;
}

export const refundsApi = {
  /** Consequence line for an existing (PENDING_APPROVAL) refund. */
  preview: (refundId: string, params: RefundPreviewParams, token: string) =>
    api.get<RefundPreviewResponse>(
      `/api/admin/refunds/${refundId}/preview?${previewQuery(params).toString()}`,
      token,
    ),

  /**
   * Consequence line before any refund row exists — the order-detail path
   * (POST /api/admin/orders/{id}/refund) creates and processes in one call, so it
   * previews by the order's paymentId instead.
   */
  previewByPayment: (paymentId: string, params: RefundPreviewParams, token: string) => {
    const query = previewQuery(params);
    query.set('paymentId', paymentId);
    return api.get<RefundPreviewResponse>(`/api/admin/refunds/preview?${query.toString()}`, token);
  },

  approve: (refundId: string, body: RefundApproveRequest, token: string) =>
    api.post<AdminRefundResponse>(`/api/admin/refunds/${refundId}/approve`, body, token),

  list: (params: RefundListParams, token: string) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.attention) query.set('attention', 'true');
    const qs = query.toString();
    return api.get<AdminRefundResponse[]>(`/api/admin/refunds${qs ? `?${qs}` : ''}`, token);
  },
};
